//  Imported under its real name: despite the `use` prefix it is a factory, not a React hook, and
//  calling it as one outside a component is what makes rules-of-hooks fire.
import createApiClient from '../interceptor';
import { financialPeriodPath } from './constants';
import { IFinancialPeriodListResponse, IFinancialPeriodWire } from './types';

const apiClient = createApiClient();

const LOCKED = 'LOCKED';

/**
 * Has finance locked the financial period covering this closeout period? (BOPS-170)
 *
 * The endpoint lists every period rather than answering about one, because that is the shape
 * BOPS-138 shipped for the enterprise Invoicing screen and there is no reason to make billing-
 * service grow a second read. There are a handful of periods per year, so the list is small.
 *
 * Returns `null` for anything that is not a clean answer. The distinction matters more here than
 * in most reads: `false` removes nothing from the screen, but `true` takes the preliminary review
 * away entirely, so a dropped connection must never be able to produce it.
 *
 * @param monthKey yyyy-MM — the month the closeout period sits in. A custom range is confined to
 *                 one calendar month by `validateCustomRange`, so one key always identifies it.
 */
export const getFinancialPeriodLock = async (monthKey: string): Promise<boolean | null> => {
  try {
    const response = await apiClient.get(financialPeriodPath.RETRIEVE_PERIODS);

    const status = Number(response?.status);
    //  The response interceptor resolves rather than rejects on everything but a 401, so the
    //  outcome is read off the status instead of a catch.
    if (!(status >= 200 && status < 300)) return null;

    const body = (response?.data ?? {}) as IFinancialPeriodListResponse;
    const periods = Array.isArray(body?.content) ? body.content : null;
    //  A body that is not the envelope we expect is unknown, not "no periods" — reading an
    //  unrecognised shape as an empty list would silently report every month unlocked.
    if (!periods) return null;

    const period = periods.find((entry: IFinancialPeriodWire) => entry?.monthKey === monthKey);

    //  Finance has never opened a period for this month, so it cannot have locked one. Distinct
    //  from a failed read above: this is a definite answer.
    if (!period) return false;

    return typeof period.status === 'string' && period.status.toUpperCase() === LOCKED;
  } catch {
    //  A dropped connection is the same outcome to the caller as a rejected read: unknown.
    return null;
  }
};
